import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list/lib/flights';
import type { MailboxInfo } from 'owa-client-ids';
import { PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID } from 'owa-folders-constants';
import type { MailFolder } from 'owa-graph-schema';
import { action } from 'satcheljs/lib/legacy';
import { getEffectiveFolderDisplayName } from '../index';
import getFolderTable from '../selectors/getFolderTable';
import getPrimaryFolderTreeRootFolder from '../selectors/getPrimaryFolderTreeRootFolder';
import type FolderStore from '../store/schema/FolderStore';
import { folderStore } from '../store/store';

export const deleteFoldersFromFolderTable = action('removeFolderFromParentFolder')(
    function deleteFoldersFromFolderTable(
        folderIdsToDelete: string[],
        mockFolderStore?: FolderStore
    ) {
        // delete each folder from local store
        const folderTable = mockFolderStore?.folderTable || getFolderTable();
        folderIdsToDelete.forEach(folderId => {
            folderTable.delete(folderId);
        });
    }
);

export let removeFolderFromParentFolder = action('removeFolderFromParentFolder')(
    function removeFolderFromParentFolder(
        folderToMove: MailFolder | undefined,
        mockFolderStore?: FolderStore
    ) {
        const folderTable = mockFolderStore?.folderTable || getFolderTable();
        if (!folderToMove?.parentFolderId) {
            return;
        }

        let parentFolder = folderTable.get(folderToMove.parentFolderId);

        // When multi-accounts experience is enabled and user has multi-accounts, there are multi-
        // root folder copies in the mailboxFolderTreeData, but only one was added to folderTable,
        // because they all have same distingish id: PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID. So
        // folderTable.get(id) probably won't get the correct root folder copy, at this time we
        // should use getPrimaryFolderTreeRootFolder(email) to retrieve it from mailboxFolderTreeData.
        if (
            isMonarchMultipleAccountsEnabled() &&
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (47,13): Object is possibly 'undefined'.
            // @ts-expect-error
            parentFolder.distinguishedFolderType === PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID
        ) {
            parentFolder = getPrimaryFolderTreeRootFolder(
                folderToMove.mailboxInfo.mailboxSmtpAddress
            );
        }

        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2532 (57,30): Object is possibly 'undefined'.
        // @ts-expect-error
        let childFolderIds = parentFolder.childFolderIds;
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2533 (61,31): Object is possibly 'null' or 'undefined'.
        // @ts-expect-error
        const folderIdIndex = childFolderIds.indexOf(folderToMove.id);
        if (folderIdIndex > -1) {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2533 (66,13): Object is possibly 'null' or 'undefined'.
            // @ts-expect-error
            childFolderIds.splice(folderIdIndex, 1);
        }
    }
);

export function addFolderInSortedOrderUnderParentFolder(
    folder: MailFolder,
    store: FolderStore,
    parentFolderId: string
): void {
    // Try to insert the folder in the alphabetically most optimal place.
    // The server will do some better alphabetical server-side sorting, but this
    // is good enough and it covers 90% of the languages and cases.
    let parentFolder = store.folderTable.get(parentFolderId);

    // When multi-accounts experience is enabled and user has multi-accounts, there are multi-
    // root folder copies in the mailboxFolderTreeData, but only one was added to folderTable,
    // because they all have same distingish id: PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID. So
    // folderTable.get(id) probably won't get the correct root folder copy, at this time we
    // should use getPrimaryFolderTreeRootFolder(email) to retrieve it from mailboxFolderTreeData.
    if (
        isMonarchMultipleAccountsEnabled() &&
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2532 (91,9): Object is possibly 'undefined'.
        // @ts-expect-error
        parentFolder.distinguishedFolderType === PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID
    ) {
        parentFolder = getPrimaryFolderTreeRootFolder(folder.mailboxInfo.mailboxSmtpAddress);
    }

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (99,9): Type 'Maybe<string[]> | undefined' is not assignable to type 'string[]'.
    // @ts-expect-error
    let childFolderIds: string[] = parentFolder.childFolderIds;
    let added: boolean = false;
    let displayNameLowerCase = getEffectiveFolderDisplayName(folder).toLowerCase();

    // Add folder if its not already added
    if (childFolderIds.indexOf(folder.id) == -1) {
        for (let i = 0; i < childFolderIds.length && !added; i++) {
            let childFolder = store.folderTable.get(childFolderIds[i]);

            // Insert item in position i, if it is next in alphabetical order.
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (112,47): Argument of type 'MailFolder | undefined' is not assignable to parameter of type 'Pick<MailFolder, "remoteFolderInfo" | "displayName">'.
            // @ts-expect-error
            if (getEffectiveFolderDisplayName(childFolder).toLowerCase() > displayNameLowerCase) {
                childFolderIds.splice(i, 0, folder.id);
                added = true;
            }
        }

        // Append at the end if needed.
        if (!added) {
            childFolderIds.push(folder.id);
        }
    }
}

export let moveFolderToParentFolder = action('moveFolderToParentFolder')(
    function moveFolderToParentFolder(
        folderToMoveId: string,
        destinationFolderId: string,
        destinationFolderMailboxInfo: MailboxInfo,
        mockFolderStore?: FolderStore // for unit testing
    ) {
        const folderTable = mockFolderStore?.folderTable || getFolderTable();
        const folderToMove = folderTable.get(folderToMoveId);
        removeFolderFromParentFolder(folderToMove, mockFolderStore);
        addFolderInSortedOrderUnderParentFolder(
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (139,13): Argument of type 'MailFolder | undefined' is not assignable to parameter of type 'MailFolder'.
            // @ts-expect-error
            folderToMove,
            mockFolderStore || folderStore,
            destinationFolderId
        );
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2532 (146,9): Object is possibly 'undefined'.
        // @ts-expect-error
        folderToMove.parentFolderId = destinationFolderId;
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2532 (150,9): Object is possibly 'undefined'.
        // @ts-expect-error
        folderToMove.mailboxInfo = destinationFolderMailboxInfo;
    }
);
